import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { Box } from '@mantine/core';
import { Link } from 'react-router-dom';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getAssociatedDocumentListQuery } from '../../../slices/thunks';
import { setRequireRefetch } from '../../../slices/associatedDocument/reducer';
import AddWaterRightDocumentModal from '../Modals/AddWaterRightDocumentModal';
import DeleteAssociatedDocumentModal from '../Modals/DeleteAssociatedDocumentModal';
import AssociatedDocumentFileModal from '../Modals/AssociatedDocumentFileModal';

const AssociatedDocumentsPanel = ({ waterRightData }) => {
	const dispatch = useDispatch();
	const [modalDocument, setModalDocument] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [modalFile, setModalFile] = useState(false);
	const [selectedDocumentData, setSelectedDocumentData] = useState(null);
	const [refetchTrigger, setRefetchTrigger] = useState(0);

	// Redux selector
	const selectorAssociatedDocument = createSelector(
		state => state.AssociatedDocument,
		associatedDocument => ({
			requireRefetch: associatedDocument?.requireRefetch,
		})
	);

	const { requireRefetch } = useSelector(selectorAssociatedDocument);

	// Check requireRefetch on mount
	useEffect(() => {
		if (requireRefetch) {
			setRefetchTrigger(prev => prev + 1);
			dispatch(setRequireRefetch(false));
		}
	}, []); // Only run on mount

	// Trigger refetch when operations complete
	useEffect(() => {
		if (requireRefetch) {
			setRefetchTrigger(prev => prev + 1);
			dispatch(setRequireRefetch(false));
		}
	}, [requireRefetch, dispatch]);

	const handleToggleDocumentModal = () => {
		setModalDocument(!modalDocument);
		if (modalDocument) {
			setSelectedDocumentData(null);
		}
	};

	const handleToggleDeleteModal = () => {
		setModalDelete(!modalDelete);
		if (modalDelete) {
			setSelectedDocumentData(null);
		}
	};

	const handleToggleFileModal = () => {
		setModalFile(!modalFile);
		if (modalFile) {
			setSelectedDocumentData(null);
		}
	};

	const handleClickAdd = () => {
		setSelectedDocumentData(null);
		setModalDocument(true);
	};

	const handleClickEdit = useCallback(documentData => {
		setSelectedDocumentData(documentData);
		setModalDocument(true);
	}, []);

	const handleClickDelete = useCallback(documentData => {
		setSelectedDocumentData(documentData);
		setModalDelete(true);
	}, []);

	const handleClickFile = useCallback(documentData => {
		setSelectedDocumentData(documentData);
		setModalFile(true);
	}, []);

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: ({ cell, row }) => {
					const documentData = row.original;
					return (
						<div className='d-flex justify-content-center'>
							<div className='flex-shrink-0'>
								<ul className='list-inline mb-0'>
									<li className='list-inline-item'>
										<Link
											title={'Editar'}
											to='#'
											onClick={() => handleClickEdit(documentData)}
										>
											<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
										</Link>
									</li>
									{documentData?.file_id && (
										<li className='list-inline-item'>
											<Link
												title='Ver Documento'
												to='#'
												onClick={() => handleClickFile(documentData)}
											>
												<i className='ri-file-search-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
									)}
									<li className='list-inline-item'>
										<Link
											title={'Eliminar'}
											to='#'
											className='remove-item-btn'
											onClick={() => handleClickDelete(documentData)}
										>
											<i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
										</Link>
									</li>
								</ul>
							</div>
						</div>
					);
				},
			},
			{
				header: 'Nombre de Documento',
				dataType: 'string',
				accessorKey: 'name',
			},
			{
				header: 'Tipo de Documento',
				dataType: 'select',
				accessorKey: 'document_type.name',
			},
		],
		[handleClickEdit, handleClickDelete, handleClickFile]
	);

	return (
		<>
			<Box
				className='p-2'
				sx={{
					display: 'Grid',
					margin: 'auto',
					width: '100%',
					overflowX: 'auto',
				}}
			>
				<ODataGrid
					columns={columns}
					getDataFunc={getAssociatedDocumentListQuery}
					baseFilters={{
						water_right_id: {
							$eq: waterRightData._id,
						},
						disable: {
							$eq: false,
						},
					}}
					queryPopulate={['document_type', 'file_id']}
					remoteOperations={{
						pagination: true,
						sorting: true,
						filtering: true,
					}}
					nameFile='documentos_asociados'
					getRowIdTable={(row) => row._id}
					refetchRemoteData={refetchTrigger}
				/>
			</Box>

			{modalDocument && (
				<AddWaterRightDocumentModal
					waterRightData={waterRightData}
					dataToEdit={selectedDocumentData}
					isOpen={modalDocument}
					handleToggleModal={handleToggleDocumentModal}
				/>
			)}

			{modalDelete && selectedDocumentData && (
				<DeleteAssociatedDocumentModal
					dataAssociatedDocument={selectedDocumentData}
					isOpen={modalDelete}
					handlerToggleModal={handleToggleDeleteModal}
				/>
			)}

			{modalFile && selectedDocumentData && (
				<AssociatedDocumentFileModal
					documentData={selectedDocumentData}
					isOpen={modalFile}
					handleToggleModal={handleToggleFileModal}
				/>
			)}
		</>
	);
};

export default AssociatedDocumentsPanel;
